// Fuzzy Arabic-name search.
//
// Goal: find a student even when the typed name is imperfect, in either
// script. All of these should match ياسين:
//   Yasi · yasin · yassine · yacine · yasseen · ياسين · يسين
// and all of these should match محمد:
//   Mohamed · mohammed · muhammad · mhamed · mhmd · محمد · محمّد
//
// How it works — four cooperating layers, from strict to forgiving:
//
//   1. Arabic-normalised form. Diacritics/tatweel stripped, alef + hamza +
//      ta-marbuta variants folded, leading "ال" dropped. Used for direct
//      Arabic-on-Arabic substring matching.
//   2. Phonetic Latin form. Arabic is romanised (Maghrebi/French style:
//      ش→ch, ق→k, خ→kh, و/ي context-sensitive) and BOTH that output and any
//      Latin query are pushed through the SAME equivalence-class canonicaliser
//      (French digraphs ph→f, ou→u, soft-c/soft-g, sh/ch unified, q→k, …),
//      so the two scripts land in one shared alphabet.
//   3. Consonant skeleton. Drop vowels + y/w and collapse doubles, so spelling
//      variants (yassine/yacine/yasin) collapse to the same consonant spine.
//   4. Damerau-Levenshtein token match. Per word, allow 1–2 edits
//      (insert/delete/substitute/transpose) scaled by length, and match words
//      out of order. This is what absorbs genuine typos.
//
// A query matches if ANY layer matches. Layers 1–3 are substring tests (so
// partial/incremental typing works); layer 4 is the typo-tolerant fallback.
// No external dependency — pure, deterministic, offline, unit-tested.

// fathatan..sukun (U+064B..U+0652), dagger alef (U+0670), tatweel (U+0640)
const DIACRITICS = /[ً-ْٰـ]/g;
const ARABIC_BLOCK = /[؀-ۿ]/;
const LATIN_COMBINING = /[̀-ͯ]/g;

const LETTER_MAP = {
  'ا': 'a', 'أ': 'a', 'إ': 'a', 'آ': 'a', 'ٱ': 'a',
  'ى': 'a', 'ة': 't',
  'ب': 'b',
  'ت': 't',
  'ث': 'th',
  'ج': 'j',
  'ح': 'h',
  'خ': 'kh',
  'د': 'd',
  'ذ': 'd',
  'ر': 'r',
  'ز': 'z',
  'س': 's',
  'ش': 'ch',
  'ص': 's',
  'ض': 'd',
  'ط': 't',
  'ظ': 'z',
  'ع': 'a',
  'غ': 'gh',
  'ف': 'f',
  'ق': 'k',
  'ك': 'k',
  'ل': 'l',
  'م': 'm',
  'ن': 'n',
  'ه': 'h',
  'ء': '',
  ' ': ' ',
  '-': ' ',
};

const isBoundaryChar = (ch) => !ch || ch === ' ' || ch === '-';

const stripArabicDiacritics = (s) =>
  String(s || '').replace(DIACRITICS, '');

// Fold the Arabic letter shapes that users (and data entry) treat as
// interchangeable, so the strict Arabic-substring lane is forgiving:
// every alef/hamza-carrier → ا, ى → ي, ة → ه, and a leading definite
// article "ال" is dropped (so الحلقة matches حلقة).
const normaliseArabic = (s) =>
  stripArabicDiacritics(s)
    .replace(/[أإآٱ]/g, 'ا')
    .replace(/[ؤئء]/g, '')
    .replace(/ى/g, 'ي')
    .replace(/ة/g, 'ه')
    .replace(/\bال/g, '')
    .replace(/\s+/g, ' ')
    .trim();

const normaliseLatin = (s) =>
  String(s || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(LATIN_COMBINING, '')
    .replace(/[^a-z0-9 ]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

// Map French/English spelling quirks onto one shared phonetic alphabet so the
// romanised Arabic and the typed Latin meet in the middle. Order matters:
// digraphs first, soft consonants before hard, then the rest.
//   sh/ch → x (sh-sound; ش romanises to "ch" and lands here too)
//   ph → f · th → t · gh → g · kh → k · dj → j · ck/qu/q → k
//   ou → u · ee → i · soft c (ce/ci/cy) → s, hard c → k
//   soft g (ge/gi/gy) → j · double letters collapse
const canonicalizeLatin = (s) =>
  String(s || '')
    .replace(/sh/g, 'x')
    .replace(/ch/g, 'x')
    .replace(/ph/g, 'f')
    .replace(/th/g, 't')
    .replace(/gh/g, 'g')
    .replace(/kh/g, 'k')
    .replace(/dj/g, 'j')
    .replace(/ck/g, 'k')
    .replace(/qu/g, 'k')
    .replace(/q/g, 'k')
    .replace(/ou/g, 'u')
    .replace(/ee/g, 'i')
    .replace(/c([eiy])/g, 's$1')
    .replace(/c/g, 'k')
    .replace(/g([eiy])/g, 'j$1')
    .replace(/(.)\1+/g, '$1')
    .replace(/\s+/g, ' ')
    .trim();

// Drop vowels + w/y and collapse duplicates → the consonant spine.
const consonantSkeleton = (s) =>
  String(s || '')
    .replace(/[aeiouyw]/g, '')
    .replace(/(.)\1+/g, '$1');

// Arabic → Maghrebi/French romanisation. و and ي are context-sensitive
// (consonantal at word start, vowel-ish elsewhere) so "Yasi" lines up with
// ياسين instead of "iasin".
export const transliterateArabic = (input) => {
  const s = stripArabicDiacritics(input);
  let out = '';
  let prev = ' ';
  for (let i = 0; i < s.length; i++) {
    const ch = s[i];
    let mapped;
    if (ch === 'ي' || ch === 'ئ') {
      mapped = isBoundaryChar(prev) ? 'y' : 'i';
    } else if (ch === 'و' || ch === 'ؤ') {
      mapped = isBoundaryChar(prev) ? 'w' : 'o';
    } else if (Object.prototype.hasOwnProperty.call(LETTER_MAP, ch)) {
      mapped = LETTER_MAP[ch];
    } else {
      mapped = ch;
    }
    out += mapped;
    prev = ch;
  }
  return out.toLowerCase();
};

// Turn any text into the shared phonetic form (canonicalised Latin).
const toPhonetic = (text) => canonicalizeLatin(normaliseLatin(transliterateArabic(text)));

const tokenize = (s) => String(s || '').split(/\s+/).filter(Boolean);

// Damerau-Levenshtein (optimal string alignment) edit distance, with an early
// exit once the running cost exceeds `max` (keeps it cheap on every keystroke).
const editDistance = (a, b, max) => {
  const al = a.length;
  const bl = b.length;
  if (Math.abs(al - bl) > max) return max + 1;
  if (al === 0) return bl;
  if (bl === 0) return al;
  let prevPrev = new Array(bl + 1).fill(0);
  let prev = new Array(bl + 1);
  let curr = new Array(bl + 1);
  for (let j = 0; j <= bl; j++) prev[j] = j;
  for (let i = 1; i <= al; i++) {
    curr[0] = i;
    let rowMin = curr[0];
    for (let j = 1; j <= bl; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      let val = Math.min(
        prev[j] + 1,        // deletion
        curr[j - 1] + 1,    // insertion
        prev[j - 1] + cost, // substitution
      );
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        val = Math.min(val, prevPrev[j - 2] + 1); // transposition
      }
      curr[j] = val;
      if (val < rowMin) rowMin = val;
    }
    if (rowMin > max) return max + 1;
    const tmp = prevPrev; prevPrev = prev; prev = curr; curr = tmp;
  }
  return prev[bl];
};

// Tolerance scales with the longer word: 3-or-fewer chars must be exact (too
// short to fuzz safely), then 1 edit up to 6 chars, 2 edits beyond.
const tolFor = (len) => (len <= 3 ? 0 : len <= 6 ? 1 : 2);

// Does query token `q` match any name token? Substring/prefix first (cheap and
// handles incremental typing), then bounded edit distance.
const tokenMatches = (nameTokens, q) => {
  if (!q) return true;
  for (const nt of nameTokens) {
    if (!nt) continue;
    if (nt.includes(q) || q.includes(nt)) return true;
    const tol = tolFor(Math.max(nt.length, q.length));
    if (tol > 0 && editDistance(nt, q, tol) <= tol) return true;
  }
  return false;
};

// Build the searchable bundle for a student (or anything with a name). Computed
// once per student (via useMemo) and reused on every keystroke.
export const buildSearchHaystack = (student) => {
  const fields = [
    student?.studentFullName,
    student?.studentFirstName,
    student?.studentLastName,
    student?.fatherFullName,
    student?.fatherFirstName,
    student?.name, // generic fallback (e.g. a class name)
  ]
    .filter(Boolean)
    .join(' ');
  const arabic = normaliseArabic(fields);
  const phonetic = toPhonetic(fields);
  const skeleton = consonantSkeleton(phonetic);
  return {
    arabic,
    phonetic,
    skeleton,
    phoneticTokens: tokenize(phonetic),
    skeletonTokens: tokenize(skeleton),
  };
};

// Strength of a match, 0 = no match. Higher = closer, so callers can rank the
// dropdown best-first. Tiers are 10 apart; a small length penalty (shorter
// name = closer) breaks ties without ever crossing a tier.
//   100 exact · 90 whole-string prefix · 85 word prefix · 80 substring
//    70 consonant-skeleton substring · 50 fuzzy (typo) · 0 none
export const nameMatchScore = (haystack, query) => {
  if (!haystack) return 0;
  const q = String(query || '').trim();
  if (!q) return 1; // no filter — neutral, keeps original order
  const penalty = Math.min(haystack.phonetic.length, 30) * 0.1;
  const tier = (base) => base - penalty;

  // Arabic query → strict Arabic-substring lane first.
  if (ARABIC_BLOCK.test(q)) {
    const qa = normaliseArabic(q);
    if (qa && haystack.arabic.includes(qa)) {
      if (haystack.arabic === qa) return tier(100);
      if (haystack.arabic.startsWith(qa)) return tier(90);
      return tier(80);
    }
    // else fall through: romanise the Arabic query and reuse the Latin lanes.
  }

  const qPhon = toPhonetic(q);
  if (!qPhon) return 0;
  if (haystack.phonetic === qPhon) return tier(100);
  if (haystack.phonetic.startsWith(qPhon)) return tier(90);
  if (haystack.phoneticTokens.some((tk) => tk.startsWith(qPhon))) return tier(85);
  if (haystack.phonetic.includes(qPhon)) return tier(80);
  const qSkel = consonantSkeleton(qPhon);
  if (qSkel && haystack.skeleton.includes(qSkel)) return tier(70);

  // Per-word fuzzy: every query word must hit some name word (order-free).
  const qPhonTokens = tokenize(qPhon);
  const qSkelTokens = tokenize(qSkel);
  const all = qPhonTokens.every((tok, i) =>
    tokenMatches(haystack.phoneticTokens, tok) ||
    tokenMatches(haystack.skeletonTokens, qSkelTokens[i] || ''),
  );
  return all ? tier(50) : 0;
};

export const nameMatches = (haystack, query) => nameMatchScore(haystack, query) > 0;
